// The spike's UI (gate A4.2), on @rpg/ui: one screen stack for the flow's screens (title, loading, pause, game over)
// and the menus over them (settings, controls, save and load slots, the sync conflict dialog). The boot flow drives
// the root screen through `flowScreens`; menus push onto the same stack. Settings and the player's bindings are kept
// in one saved value (`settings`) and synced. The rebinding screen runs on the input's own rebinding model
// (./rebind-model.ts over @rpg/runtime's `Rebinding`); the hold-or-toggle settings set its hold modes.
import type { WebGLRenderer } from "three";
import { createUi, UI_FAULTS, safeInsets, type NavIntent, type ScreenRender, type Ui } from "@rpg/ui";
import { askWithDialog } from "@rpg-unit/save-slots";
import { frontEndFlow } from "@rpg-unit/front-end-flow";
import type { FlowScreens } from "../boot";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import type { SpikeSaves } from "../save";
import { controlLabel } from "./controls";
import type { Rebinding } from "@rpg/runtime";
import { createRebindModel } from "./rebind-model";
import { spikeScreens } from "./screens";
import { bindHoldModes, mountSettings, persistSettings } from "@rpg-unit/settings";

/** The UI's planted defect names, for the spike's one `?faults=` switch. */
export const UI_FAULT_NAMES = ["unlabelled-button"] as const;

/** The valley palette for screens (UI art in code). */
const THEME = {
  font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  text: "#fbf6e8",
  muted: "#dcd5c0",
  bg: "#1d2a1f",
  panel: "rgba(29, 42, 31, 0.95)",
  scrim: "rgba(16, 24, 18, 0.55)",
  splash: "linear-gradient(rgba(29, 42, 31, 0.2), rgba(29, 42, 31, 0.7))",
  button: "#30473a",
  buttonHover: "#3e5a49",
  accent: "#e9c46a",
  accentText: "#1d2a1f",
  focus: "#9fd8ff",
  warn: "#ffc2b3",
  radius: "12px",
};

/** What the UI needs from the game. */
export interface UiDeps {
  content: SpikeContent;
  saves: SpikeSaves;
  renderer: WebGLRenderer;
  /** Is this a phone (the rotate prompt), and its tier (boot's pick). */
  phone: () => boolean;
  deviceTier: () => string;
  /** Pause play before a dialog asks the player something (a sync conflict). */
  pauseIfPlaying: () => void;
  /** Settings write delay, ms (tuning `save.settingsWriteDelay`). */
  writeDelayMs: number;
  /** The input's rebinding model (src/input, gate A2.1). */
  rebinding: Rebinding;
  search?: string;
  /** Screens other modules add (phase D2: the recap). */
  screens?: Record<string, ScreenRender>;
}

/** Mount the UI; returns the flow's screens, the handle keys and the fault group. */
export function mountUi(d: UiDeps) {
  const reg = d.content.registry;
  // the 3D view is not screen-reader playable (decision 0063): hide it from assistive tech; the screens carry the text
  d.renderer.domElement.setAttribute("aria-hidden", "true");
  const t = d.content.t;
  const label = (c: string): string => controlLabel(t, (id) => d.content.strings.has(id), c);
  const rebind = createRebindModel({ rebinding: d.rebinding, actions: reg.rows("actions").map((a) => ({ id: a.id, label: a.label.id })) });

  // the screens read `deps.settings` at render time; it is set right after the UI exists
  const deps = { saves: d.saves, rebind, controlLabel: label } as Parameters<typeof spikeScreens>[0];
  const s = spikeScreens(deps);
  const ui: Ui = createUi({
    t,
    // one screen table, shared with addScreens below (a copy would miss the rpg layer's screens added later)
    screens: Object.assign(s.screens, d.screens ?? {}),
    labels: { back: "ui.back", rotate: "ui.rotate" },
    theme: THEME,
    phone: d.phone,
    onChange: (c) => {
      if (c.to && c.op !== "pop") s.opened(c.to, ui);
    },
  });
  const tuning = (id: string): number => {
    const row = reg.get("tuning", id);
    if (!row) throw new Error(`no tuning value ${id}`);
    return row.value;
  };
  // the settings feature (@rpg-unit/settings) over the standard rows (@rpg-unit/standard-settings' `settings` table) and
  // the game's own (`game-settings`), the standard rows first in each section
  const rows = [...reg.rows("settings"), ...reg.rows("game-settings")];
  const { settings, applied } = mountSettings(rows, {
    ui,
    textScale: (size) => tuning(`ui.textScale.${size}`),
    deviceTier: d.deviceTier,
    quality: (tier) => d.renderer.setPixelRatio(tier === "low" ? 1 : Math.min(devicePixelRatio, 2)),
  });
  deps.settings = settings;
  // hold or toggle (decision 0063): the input's model holds the mode (stored and synced with its bindings); the
  // settings' `controls.<action>` choice sets it, and mirrors it when it changes elsewhere (the handle, a sync)
  const hold = bindHoldModes(settings, rows, d.rebinding);

  // settings and bindings: loaded once, then written (debounced) on every change and synced; settings changed on
  // another device arrive with a sync and are applied
  const { ready } = persistSettings({
    settings,
    bindings: rebind,
    delayMs: d.writeDelayMs,
    store: {
      load: () => d.saves.loadSettings(),
      save: (v) => d.saves.saveSettings(v),
      onPulled: (fn) => d.saves.onPulled((key) => key === "settings" && fn()),
    },
    afterLoad: () => hold.sync(), // the input's modes (in the saved bindings) are the ones in force
    settled: () => ui.refresh(),
  });

  // signing in or out (here or in another tab) redraws the account screen and the slot lists
  d.saves.account.onChange(() => ui.refresh());

  // a sync conflict asks the player (decision 0033), over whatever is showing (@rpg-unit/save-slots' dialog)
  askWithDialog({ saves: d.saves, ui, pause: d.pauseIfPlaying });

  // The title reads the slot list first (Continue shows, focused, when an autosave exists); a flow change made while it
  // reads wins (@rpg-unit/front-end-flow's flow screens).
  const flowScreens: FlowScreens = frontEndFlow(ui, { beforeTitle: () => s.refreshSlots(ui) });

  // the planted defect: icon buttons lose their accessible name
  const on = (new URLSearchParams(d.search ?? location.search).get("faults") ?? "").split(",");
  UI_FAULTS.unlabelledButton = on.includes("unlabelled-button");
  const faultGroup: FaultGroup = {
    names: UI_FAULT_NAMES,
    get: () => UI_FAULTS.unlabelledButton,
    set: (_n, value) => {
      UI_FAULTS.unlabelledButton = value;
      ui.refresh();
    },
  };

  const handle = {
    ui: {
      top: () => ui.top(),
      ids: () => ui.ids(),
      renders: () => ui.renders(),
      push: (id: string, params?: unknown) => ui.push(id, params),
      back: () => ui.back(),
      nav: (intent: NavIntent) => ui.nav(intent),
      rotatePrompt: () => ui.rotatePrompt(),
      safeInsets: () => safeInsets(ui.root),
      /** Settings and bindings loaded from the save store. */
      ready: () => ready.then(() => true),
      settings: { values: () => settings.values(), set: (id: string, v: number | boolean | string) => settings.set(id, v), applied },
      rebind: { overrides: () => rebind.overrides(), conflicts: (device?: string) => rebind.conflicts(device), capturing: () => rebind.capturing() },
    },
  };
  // phase D1: a scene's own screens join the stack's (the rpg layer's inventory, skills, shop, forge and pot), and its
  // pause-menu entries the pause screen
  const addScreens = (more: Record<string, ScreenRender>, extras: { label: string; action: string; screen: string }[] = []): void => {
    // the UI keeps its own table since the hud stream's `addScreen` (phase D joined: each screen added there too)
    Object.assign(s.screens, more);
    for (const [id, render] of Object.entries(more)) ui.addScreen(id, render);
    for (const x of extras) s.addPauseEntry({ label: x.label, action: x.action, order: 10, press: (ctx) => ctx.ui.push(x.screen) });
    ui.refresh();
  };
  return { ui, flowScreens, settings, rebind, handle, faultGroup, addScreens };
}
